import { useEffect, useMemo, useState } from "react";

const REPORT_JSON_BASE =
  "https://educationforall.app.n8n.cloud/webhook/report-client-analytics";

const DEFAULT_RANGE = "last_30_days";

type ReportPayload = Record<string, unknown>;

type ModeBlock = {
  count: number;
  percent: number;
};

type DisplayRow = {
  key: string;
  label: string;
  count: number;
  percent: number;
};

type ActivityTypeRow = {
  activityFamily?: string;
  activityType?: string;
  count?: number;
  startedCount?: number;
  completedCount?: number;
  submittedCount?: number;
  thoughtSubmittedCount?: number;
};

type EventTypeRow = {
  eventType?: string;
  safetyEventType?: string; // backward compatibility for old responses
  count?: number;
};

type SafetyIncidentDetail = {
  timestamp?: string;
  sessionId?: string;
  eventKind?: string;
  eventType?: string | null;
  safetyEventType?: string | null; // backward compatibility for old responses
  safetyEventDetected?: boolean;
  reassuranceDetected?: boolean;
  detectedOnSender?: string;
  detectedMessage?: string | null;
  userMessageAt?: string | null;
  userMessage?: string | null;
  assistantResponseAt?: string | null;
  assistantResponse?: string | null;
};

type ChartSlice = {
  label: string;
  value: number;
  color: string;
};

type TabKey = "overview" | "activities" | "safety";

type RangeKey = "today" | "last_7_days" | "last_30_days" | "all_time";

const TAB_OPTIONS: Array<{
  key: TabKey;
  label: string;
  alertDot?: boolean;
}> = [
    { key: "overview", label: "Communication & Devices" },
    { key: "activities", label: "Tool Usage" },
    { key: "safety", label: "Safety & Reassurance", alertDot: true },
  ];

const RANGE_OPTIONS: Array<{
  key: RangeKey;
  label: string;
}> = [
    { key: "today", label: "Today" },
    { key: "last_7_days", label: "7D" },
    { key: "last_30_days", label: "30D" },
    { key: "all_time", label: "All" },
  ];

type SegmentOption<K extends string> = {
  key: K;
  label: string;
  alertDot?: boolean;
};

function SegmentedControl<K extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: Array<SegmentOption<K>>;
  value: K;
  onChange: (key: K) => void;
  ariaLabel: string;
}) {
  return (
    <div
      className="inline-flex w-full rounded-full bg-slate-100 p-1 md:w-auto"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const isActive = value === option.key;

        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.key)}
            className={[
              "inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full px-3 py-2.5 text-sm font-bold uppercase tracking-wide transition md:flex-none lg:px-4",
              isActive
                ? "bg-slate-950 text-white shadow-sm"
                : "text-slate-600 hover:bg-white/60 hover:text-slate-950",
            ].join(" ")}
          >
            {option.alertDot ? (
              <span
                className="h-2.5 w-2.5 rounded-full bg-amber-400"
                aria-hidden="true"
              />
            ) : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function normalizeRange(v: string | null): RangeKey {
  const range = (v ?? "").toLowerCase().trim();

  if (range === "today") return "today";
  if (range === "last_7_days") return "last_7_days";
  if (range === "all_time" || range === "all") return "all_time";

  return "last_30_days";
}

function normalizeTab(v: string | null): TabKey {
  const tab = (v ?? "").toLowerCase().trim();

  // Backward compatibility with the old Airtable formula.
  if (tab === "modality") return "overview";

  if (tab === "activities") return "activities";
  if (tab === "safety") return "safety";

  return "overview";
}

function str(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return undefined;
}

function csvEscape(value: unknown): string {
  const s = String(value ?? "");
  const escaped = s.replace(/"/g, '""');

  return /[",\n\r]/.test(s) ? `"${escaped}"` : escaped;
}

function pickNumber(j: ReportPayload, keys: string[]): number {
  for (const k of keys) {
    const v = j[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) {
      return Number(v);
    }
  }
  return 0;
}

function pickString(j: ReportPayload, keys: string[]): string | undefined {
  for (const k of keys) {
    const s = str(j[k]);
    if (s !== undefined && s !== "") return s;
  }
  return undefined;
}

function readBlock(j: ReportPayload, keys: string[]): ModeBlock | null {
  for (const k of keys) {
    const block = j[k];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const o = block as Record<string, unknown>;
      return {
        count: pickNumber(o, ["count", "total", "n", "messages"]),
        percent: pickNumber(o, ["percent", "percentage", "pct", "share"]),
      };
    }
  }
  return null;
}

function getObject(j: ReportPayload | null, key: string): ReportPayload | null {
  const v = j?.[key];
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as ReportPayload)
    : null;
}

function getArray<T = ReportPayload>(obj: ReportPayload | null, key: string): T[] {
  const v = obj?.[key];
  return Array.isArray(v) ? (v as T[]) : [];
}

function humanizeKey(v: unknown): string {
  const s = str(v) ?? "";
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatReportDate(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Los_Angeles",
    timeZoneName: "short",
  }).format(date);
}

function getEventType(v: {
  eventType?: string | null;
  safetyEventType?: string | null;
}): string | undefined {
  return v.eventType ?? v.safetyEventType ?? undefined;
}

function isTrueSafetyIncident(item: SafetyIncidentDetail): boolean {
  const eventType = getEventType(item);
  return item.safetyEventDetected === true && eventType !== "reassurance_seeking";
}

function buildRows(j: ReportPayload): DisplayRow[] {
  const specs: { key: string; label: string; jsonKeys: string[] }[] = [
    {
      key: "typed",
      label: "Typed",
      jsonKeys: ["typed", "text", "type", "keyboard"],
    },
    {
      key: "dictation",
      label: "Dictation",
      jsonKeys: ["dictation", "speechRecognition", "speech_recognition"],
    },
    {
      key: "realtimeVoice",
      label: "Real-time voice",
      jsonKeys: ["realtimeVoice", "realtime_voice", "realtime", "voice"],
    },
    {
      key: "unknown",
      label: "Unknown",
      jsonKeys: ["unknown", "other", "null", "unset"],
    },
  ];

  const rows: DisplayRow[] = [];

  for (const spec of specs) {
    const b = readBlock(j, spec.jsonKeys);
    rows.push({
      key: spec.key,
      label: spec.label,
      count: b?.count ?? 0,
      percent: b?.percent ?? 0,
    });
  }

  const hasAny =
    rows.some((r) => r.count > 0 || r.percent > 0) ||
    specs.some((s) => readBlock(j, s.jsonKeys) != null);

  if (hasAny) return rows;

  const arr = j.rows ?? j.breakdown ?? j.modes ?? j.byMode;
  if (!Array.isArray(arr)) return rows;

  const merged: Record<string, ModeBlock> = {};

  for (const item of arr) {
    if (!item || typeof item !== "object") continue;

    const o = item as Record<string, unknown>;
    const modeRaw =
      str(o.mode) ?? str(o.inputMode) ?? str(o.type) ?? str(o.label) ?? "";
    const mode = modeRaw.toLowerCase().replace(/\s+/g, "_");
    const count = pickNumber(o, ["count", "total", "n"]);
    const percent = pickNumber(o, ["percent", "percentage", "pct"]);

    let key = "unknown";

    if (/typed|text|keyboard|^type$/.test(mode)) {
      key = "typed";
    } else if (/dictation|speech|webkit/.test(mode)) {
      key = "dictation";
    } else if (
      /realtime_voice|realtimevoice|realtime/.test(mode) &&
      !/dictation/.test(mode)
    ) {
      key = "realtimeVoice";
    } else if (/unknown|other|null/.test(mode)) {
      key = "unknown";
    }

    const prev = merged[key] ?? { count: 0, percent: 0 };
    merged[key] = {
      count: prev.count + count,
      percent: percent || prev.percent,
    };
  }

  return specs.map((spec) => ({
    key: spec.key,
    label: spec.label,
    count: merged[spec.key]?.count ?? 0,
    percent: merged[spec.key]?.percent ?? 0,
  }));
}

function buildConicGradient(slices: ChartSlice[]): string {
  const total = slices.reduce((sum, s) => sum + s.value, 0);

  if (total <= 0) {
    return "conic-gradient(#e5e7eb 0deg 360deg)";
  }

  let current = 0;
  const parts: string[] = [];

  for (const slice of slices) {
    const angle = (slice.value / total) * 360;
    const start = current;
    const end = current + angle;
    parts.push(`${slice.color} ${start}deg ${end}deg`);
    current = end;
  }

  return `conic-gradient(${parts.join(", ")})`;
}

function DonutChart({
  title,
  slices,
}: {
  title: string;
  slices: ChartSlice[];
}) {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const background = buildConicGradient(slices);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative h-40 w-40 shrink-0">
          <div
            className="h-40 w-40 rounded-full [print-color-adjust:exact] [-webkit-print-color-adjust:exact]"
            style={{ background }}
          />
          <div className="absolute inset-8 flex items-center justify-center rounded-full bg-white text-center">
            <div>
              <div className="text-xs uppercase text-slate-500">Total</div>
              <div className="text-xl font-semibold tabular-nums text-slate-900">
                {total}
              </div>
            </div>
          </div>
        </div>

        <ul className="space-y-2 text-sm">
          {slices.map((slice) => {
            const pct = total > 0 ? (slice.value / total) * 100 : 0;

            return (
              <li key={slice.label} className="flex items-center gap-3">
                <span
                  className="inline-block h-3 w-3 rounded-sm [print-color-adjust:exact] [-webkit-print-color-adjust:exact]"
                  style={{ backgroundColor: slice.color }}
                />
                <span className="min-w-[140px] text-slate-700">
                  {slice.label}
                </span>
                <span className="font-medium tabular-nums text-slate-900">
                  {slice.value}
                </span>
                <span className="text-slate-500">({pct.toFixed(1)}%)</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function SafetyIncidentDetails({
  details,
}: {
  details: SafetyIncidentDetail[];
}) {
  if (!details.length) {
    return (
      <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
        No safety or reassurance incident details found for this date range.
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">
          Safety & Reassurance Detection Details
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          Recent safety and reassurance detections with nearby client and assistant messages.
        </p>
      </div>

      <div className="divide-y divide-slate-100">
        {details.map((item, index) => (
          <details
            key={`${item.timestamp ?? "incident"}-${index}`}
            className="group"
          >
            <summary className="cursor-pointer list-none px-4 py-3 hover:bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-transform group-open:rotate-90"
                  >
                    ▶
                  </span>

                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">
                      {item.eventKind ?? "Safety Event"}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {item.timestamp ?? ""}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  {isTrueSafetyIncident(item) && (
                    <span className="rounded-full bg-red-50 px-2 py-1 font-medium text-red-700 ring-1 ring-red-100">
                      Safety
                    </span>
                  )}

                  {item.reassuranceDetected && (
                    <span className="rounded-full bg-amber-50 px-2 py-1 font-medium text-amber-700 ring-1 ring-amber-100">
                      Reassurance
                    </span>
                  )}

                  {getEventType(item) && (
                    <span className="rounded-full bg-slate-100 px-2 py-1 font-medium text-slate-700">
                      {humanizeKey(getEventType(item) ?? "")}
                    </span>
                  )}
                </div>
              </div>
            </summary>

            <div className="space-y-4 px-4 pb-4 text-sm">
              <div>
                <div className="text-xs font-semibold uppercase text-slate-500">
                  User message
                </div>
                <div className="mt-1 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-slate-800">
                  {item.userMessage ||
                    item.detectedMessage ||
                    "No user message found."}
                </div>
                {item.userMessageAt && (
                  <div className="mt-1 text-xs text-slate-500">
                    {item.userMessageAt}
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs font-semibold uppercase text-slate-500">
                  Assistant response
                </div>
                <div className="mt-1 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-slate-800">
                  {item.assistantResponse ||
                    "No following assistant response found."}
                </div>
                {item.assistantResponseAt && (
                  <div className="mt-1 text-xs text-slate-500">
                    {item.assistantResponseAt}
                  </div>
                )}
              </div>

              {item.detectedMessage &&
                item.detectedMessage !== item.userMessage && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">
                      Detected message
                    </div>
                    <div className="mt-1 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-slate-800">
                      {item.detectedMessage}
                    </div>
                  </div>
                )}

              <div className="grid gap-2 rounded-lg border border-slate-100 bg-white p-3 text-xs text-slate-600 sm:grid-cols-2">
                <div>
                  <span className="font-semibold text-slate-700">Session:</span>{" "}
                  {item.sessionId ?? "—"}
                </div>
                <div>
                  <span className="font-semibold text-slate-700">
                    Detected on:
                  </span>{" "}
                  {item.detectedOnSender ?? "—"}
                </div>
              </div>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

function buildReportUrl(accessCode: string, range: string): string {
  const u = new URL(REPORT_JSON_BASE);
  u.searchParams.set("accessCode", accessCode);
  u.searchParams.set("range", range);
  return u.toString();
}

export default function ClientAnalyticsReportPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const accessCode = (params.get("accessCode") ?? "").trim();
  const nickname = (params.get("nickname") ?? "").trim();

  const [range, setRangeState] = useState<RangeKey>(() =>
    normalizeRange(params.get("range"))
  );

  const [activeTab, setActiveTabState] = useState<TabKey>(() =>
    normalizeTab(params.get("tab"))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<ReportPayload | null>(null);

  function setActiveTab(tab: TabKey) {
    setActiveTabState(tab);

    const next = new URLSearchParams(window.location.search);
    next.set("tab", tab);

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${next.toString()}`
    );
  }

  function setRange(nextRange: RangeKey) {
    setRangeState(nextRange);

    const next = new URLSearchParams(window.location.search);
    next.set("range", nextRange);

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${next.toString()}`
    );
  }

  function saveCurrentTabPdf() {
    const previousTitle = document.title;

    const safeAccessCode = accessCode.replace(/[^a-z0-9_-]/gi, "_");
    const safeRange = range.replace(/[^a-z0-9_-]/gi, "_");
    const safeTab = activeTab.replace(/[^a-z0-9_-]/gi, "_");

    document.title = `client-usage-analytics-${safeAccessCode}-${safeTab}-${safeRange}`;

    window.print();

    window.setTimeout(() => {
      document.title = previousTitle;
    }, 500);
  }

  useEffect(() => {
    const beforePrint = () => {
      if (activeTab !== "safety") return;

      document
        .querySelectorAll<HTMLDetailsElement>("details")
        .forEach((detail) => {
          detail.dataset.wasOpen = detail.open ? "true" : "false";
          detail.open = true;
        });
    };

    const afterPrint = () => {
      document
        .querySelectorAll<HTMLDetailsElement>("details")
        .forEach((detail) => {
          detail.open = detail.dataset.wasOpen === "true";
          delete detail.dataset.wasOpen;
        });
    };

    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);

    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, [activeTab]);

  useEffect(() => {
    if (!accessCode) {
      setError("Missing required query parameter: accessCode");
      setPayload(null);
      return;
    }

    let cancelled = false;
    const url = buildReportUrl(accessCode, range);

    (async () => {
      setLoading(true);
      setError(null);
      setPayload(null);

      try {
        const res = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        const text = await res.text();

        let data: unknown = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          throw new Error("Response was not valid JSON");
        }

        if (cancelled) return;

        if (!res.ok) {
          const msg =
            typeof data === "object" && data !== null && "message" in data
              ? str((data as ReportPayload).message)
              : undefined;

          throw new Error(msg || `Request failed (${res.status})`);
        }

        const root =
          Array.isArray(data) && data.length > 0 && typeof data[0] === "object"
            ? (data[0] as ReportPayload)
            : (data as ReportPayload);

        setPayload(root);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessCode, range]);

  const start =
    payload &&
    (pickString(payload, ["start", "startDate", "periodStart", "from", "dateStart"]) ??
      str(payload.start_iso) ??
      str(payload.startIso));

  const end =
    payload &&
    (pickString(payload, ["end", "endDate", "periodEnd", "to", "dateEnd"]) ??
      str(payload.end_iso) ??
      str(payload.endIso));

  const inputModes = getObject(payload, "inputModes");

  const total =
    inputModes != null
      ? pickNumber(inputModes, [
        "totalUserMessages",
        "total_user_messages",
        "totalMessages",
        "total_messages",
        "total",
      ])
      : 0;

  const modeRows = inputModes ? buildRows(inputModes) : [];

  const assistantOutputModes = getObject(payload, "assistantOutputModes");

  const assistantTotal =
    assistantOutputModes != null
      ? pickNumber(assistantOutputModes, [
        "totalAssistantMessages",
        "total_assistant_messages",
        "totalMessages",
        "total",
      ])
      : 0;

  const assistantRows: DisplayRow[] = assistantOutputModes
    ? [
      {
        key: "text",
        label: "Text",
        count: readBlock(assistantOutputModes, ["text"])?.count ?? 0,
        percent: readBlock(assistantOutputModes, ["text"])?.percent ?? 0,
      },
      {
        key: "voicePlayback",
        label: "Voice playback",
        count:
          readBlock(assistantOutputModes, ["voicePlayback", "voice_playback"])
            ?.count ?? 0,
        percent:
          readBlock(assistantOutputModes, ["voicePlayback", "voice_playback"])
            ?.percent ?? 0,
      },
      {
        key: "unknown",
        label: "Unknown",
        count: readBlock(assistantOutputModes, ["unknown"])?.count ?? 0,
        percent: readBlock(assistantOutputModes, ["unknown"])?.percent ?? 0,
      },
    ]
    : [];

  const userInputChartSlices: ChartSlice[] = [
    {
      label: "Typed",
      value: modeRows.find((r) => r.key === "typed")?.count ?? 0,
      color: "#3b82f6",
    },
    {
      label: "Dictation",
      value: modeRows.find((r) => r.key === "dictation")?.count ?? 0,
      color: "#10b981",
    },
    {
      label: "Real-time voice",
      value: modeRows.find((r) => r.key === "realtimeVoice")?.count ?? 0,
      color: "#f59e0b",
    },
    {
      label: "Unknown",
      value: modeRows.find((r) => r.key === "unknown")?.count ?? 0,
      color: "#94a3b8",
    },
  ];

  const assistantOutputChartSlices: ChartSlice[] = [
    {
      label: "Text",
      value: assistantRows.find((r) => r.key === "text")?.count ?? 0,
      color: "#6366f1",
    },
    {
      label: "Voice playback",
      value: assistantRows.find((r) => r.key === "voicePlayback")?.count ?? 0,
      color: "#ec4899",
    },
    {
      label: "Unknown",
      value: assistantRows.find((r) => r.key === "unknown")?.count ?? 0,
      color: "#94a3b8",
    },
  ];

  const devices = getObject(payload, "devices");

  const deviceTotal =
    devices != null
      ? pickNumber(devices, [
        "totalUserMessages",
        "total_user_messages",
        "totalMessages",
        "total_messages",
        "total",
      ])
      : 0;

  const deviceRows: DisplayRow[] = devices
    ? [
      {
        key: "iphone",
        label: "iPhone",
        count: readBlock(devices, ["iphone", "iPhone"])?.count ?? 0,
        percent: readBlock(devices, ["iphone", "iPhone"])?.percent ?? 0,
      },
      {
        key: "androidPhone",
        label: "Android phone",
        count:
          readBlock(devices, [
            "androidPhone",
            "android_phone",
            "androidphone",
          ])?.count ?? 0,
        percent:
          readBlock(devices, [
            "androidPhone",
            "android_phone",
            "androidphone",
          ])?.percent ?? 0,
      },
      {
        key: "ipad",
        label: "iPad",
        count: readBlock(devices, ["ipad", "iPad"])?.count ?? 0,
        percent: readBlock(devices, ["ipad", "iPad"])?.percent ?? 0,
      },
      {
        key: "androidTablet",
        label: "Android tablet",
        count:
          readBlock(devices, [
            "androidTablet",
            "android_tablet",
            "androidtablet",
          ])?.count ?? 0,
        percent:
          readBlock(devices, [
            "androidTablet",
            "android_tablet",
            "androidtablet",
          ])?.percent ?? 0,
      },
      {
        key: "desktopLaptop",
        label: "Desktop/laptop",
        count:
          readBlock(devices, [
            "desktopLaptop",
            "desktop_laptop",
            "desktop",
            "laptop",
          ])?.count ?? 0,
        percent:
          readBlock(devices, [
            "desktopLaptop",
            "desktop_laptop",
            "desktop",
            "laptop",
          ])?.percent ?? 0,
      },
      {
        key: "unknown",
        label: "Unknown",
        count:
          readBlock(devices, [
            "unknown",
            "other",
            "null",
            "unset",
          ])?.count ?? 0,
        percent:
          readBlock(devices, [
            "unknown",
            "other",
            "null",
            "unset",
          ])?.percent ?? 0,
      },
    ]
    : [];

  const deviceChartSlices: ChartSlice[] = [
    {
      label: "iPhone",
      value: deviceRows.find((r) => r.key === "iphone")?.count ?? 0,
      color: "#2563eb",
    },
    {
      label: "Android phone",
      value: deviceRows.find((r) => r.key === "androidPhone")?.count ?? 0,
      color: "#16a34a",
    },
    {
      label: "iPad",
      value: deviceRows.find((r) => r.key === "ipad")?.count ?? 0,
      color: "#7c3aed",
    },
    {
      label: "Android tablet",
      value: deviceRows.find((r) => r.key === "androidTablet")?.count ?? 0,
      color: "#0891b2",
    },
    {
      label: "Desktop/laptop",
      value: deviceRows.find((r) => r.key === "desktopLaptop")?.count ?? 0,
      color: "#f59e0b",
    },
    {
      label: "Unknown",
      value: deviceRows.find((r) => r.key === "unknown")?.count ?? 0,
      color: "#94a3b8",
    },
  ];

  const activities = getObject(payload, "activities");
  const activityByType = getArray<ActivityTypeRow>(activities, "byType");

  const safety = getObject(payload, "safety");
  const safetyByType = getArray<EventTypeRow>(safety, "byType");
  const safetyDetails = getArray<SafetyIncidentDetail>(safety, "details");

  const totalSafetyEvents = safety ? pickNumber(safety, ["totalSafetyEvents"]) : 0;
  const reassuranceDetections = safety
    ? pickNumber(safety, ["reassuranceDetections"])
    : 0;
  const sessionsWithSafetyEvents = safety
    ? pickNumber(safety, ["sessionsWithSafetyEvents"])
    : 0;
  const sessionsWithReassurance = safety
    ? pickNumber(safety, ["sessionsWithReassurance"])
    : 0;
  const hasSafetyDetections =
    totalSafetyEvents > 0 || sessionsWithSafetyEvents > 0;
  const hasReassuranceDetections =
    reassuranceDetections > 0 || sessionsWithReassurance > 0;
  const safetyMetricCardClass = hasSafetyDetections
    ? "rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm"
    : "rounded-xl border border-slate-200 bg-white p-4 shadow-sm";
  const reassuranceMetricCardClass = hasReassuranceDetections
    ? "rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm"
    : "rounded-xl border border-slate-200 bg-white p-4 shadow-sm";
  const lastSafetyEventAt = safety
    ? pickString(safety, ["lastSafetyEventAt"])
    : undefined;
  const lastReassuranceAt = safety
    ? pickString(safety, ["lastReassuranceAt"])
    : undefined;

  const notes =
    payload?.notes && Array.isArray(payload.notes)
      ? payload.notes.filter((n): n is string => typeof n === "string")
      : [];

  const note = payload ? pickString(payload, ["note"]) : undefined;

  function exportActivityUsageCsv() {
    const headers = ["Feature", "Tool", "Started", "Completed", "Submitted"];

    const rows = activityByType.map((row) => [
      humanizeKey(row.activityFamily),
      humanizeKey(row.activityType),
      row.startedCount ?? 0,
      row.completedCount ?? 0,
      row.submittedCount ?? 0,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");

    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    const safeAccessCode = accessCode.replace(/[^a-z0-9_-]/gi, "_");
    const safeRange = range.replace(/[^a-z0-9_-]/gi, "_");
    const exportedAt = new Date().toISOString().slice(0, 10);

    a.href = url;
    a.download = `tool-usage-${safeAccessCode}-${safeRange}-${exportedAt}.csv`;

    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  }

  return (
    <div className="h-screen overflow-y-auto bg-slate-50 text-slate-900 print:h-auto print:overflow-visible">
      <div className="mx-auto max-w-5xl px-4 py-10 print:max-w-none print:px-0 print:py-0">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {nickname ? `${nickname} — Usage Analytics` : "Client Usage Analytics"}
        </h1>

        <p className="mt-2 text-sm text-slate-600">
          Review communication modes, device usage, interactive tool usage, and
          safety and reassurance detections for this client.
        </p>

        {!accessCode && (
          <div
            className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            role="alert"
          >
            Add <code className="rounded bg-amber-100/80 px-1">?accessCode=…</code>{" "}
            to the URL.
          </div>
        )}

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-600">
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"
              aria-hidden
            />
            Loading report…
          </div>
        )}

        {error && accessCode && (
          <div
            className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
            role="alert"
          >
            {error}
          </div>
        )}

        {!loading && !error && payload && accessCode && (
          <div className="mt-8 space-y-8 pb-12">
            <dl className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium uppercase text-slate-500">
                  Access code
                </dt>
                <dd className="mt-0.5 font-medium text-slate-900">{accessCode}</dd>
              </div>

              <div>
                <dt className="text-xs font-medium uppercase text-slate-500">
                  Range
                </dt>
                <dd className="mt-0.5 font-medium text-slate-900">
                  {RANGE_OPTIONS.find((option) => option.key === range)?.label ?? range}
                </dd>
              </div>

              {start && (
                <div>
                  <dt className="text-xs font-medium uppercase text-slate-500">
                    Start
                  </dt>
                  <dd className="mt-0.5 text-slate-800">
                    {formatReportDate(start)}
                  </dd>
                </div>
              )}

              {end && (
                <div>
                  <dt className="text-xs font-medium uppercase text-slate-500">
                    End
                  </dt>
                  <dd className="mt-0.5 text-slate-800">
                    {formatReportDate(end)}
                  </dd>
                </div>
              )}

              <div className="sm:col-span-2">
                <dt className="text-xs font-medium uppercase text-slate-500">
                  Total client messages
                </dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
                  {total}
                </dd>
              </div>
            </dl>

            <div>
              <div className="mb-8 rounded-[28px] border border-slate-200/80 bg-white/80 p-2 shadow-sm backdrop-blur print:hidden">
                <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
                  <div className="flex min-w-0 flex-col items-start gap-2">
                    <SegmentedControl<TabKey>
                      options={TAB_OPTIONS}
                      value={activeTab}
                      onChange={setActiveTab}
                      ariaLabel="Usage analytics section"
                    />

                    <SegmentedControl<RangeKey>
                      options={RANGE_OPTIONS}
                      value={range}
                      onChange={setRange}
                      ariaLabel="Date range"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={saveCurrentTabPdf}
                    disabled={!payload}
                    title={`Save the ${activeTab} report as a PDF`}
                    className="shrink-0 whitespace-nowrap rounded-full bg-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Save PDF
                  </button>
                </div>
              </div>

              {activeTab === "overview" && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Client input modes
                    </h2>

                    <ul className="mt-3 grid gap-3 sm:grid-cols-2">
                      {modeRows.map((row) => (
                        <li
                          key={row.key}
                          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                        >
                          <div className="text-sm font-medium text-slate-900">
                            {row.label}
                          </div>
                          <div className="mt-2 flex items-baseline gap-2 tabular-nums">
                            <span className="text-2xl font-semibold text-slate-900">
                              {row.count}
                            </span>
                            <span className="text-sm text-slate-500">
                              ({row.percent.toFixed(1)}%)
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-4">
                      <DonutChart
                        title="Client input mode distribution"
                        slices={userInputChartSlices}
                      />
                    </div>
                  </div>

                  {assistantRows.length > 0 && (
                    <div>
                      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                        Assistant output modes
                      </h2>

                      <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm">
                        <dt className="text-xs font-medium uppercase text-slate-500">
                          Total assistant messages
                        </dt>
                        <dd className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
                          {assistantTotal}
                        </dd>
                      </div>

                      <ul className="mt-3 grid gap-3 sm:grid-cols-3">
                        {assistantRows.map((row) => (
                          <li
                            key={row.key}
                            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                          >
                            <div className="text-sm font-medium text-slate-900">
                              {row.label}
                            </div>
                            <div className="mt-2 flex items-baseline gap-2 tabular-nums">
                              <span className="text-2xl font-semibold text-slate-900">
                                {row.count}
                              </span>
                              <span className="text-sm text-slate-500">
                                ({row.percent.toFixed(1)}%)
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-4">
                        <DonutChart
                          title="Assistant output mode distribution"
                          slices={assistantOutputChartSlices}
                        />
                      </div>
                    </div>
                  )}

                  {devices && (
                    <div>
                      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                        Client devices
                      </h2>

                      <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm">
                        <dt className="text-xs font-medium uppercase text-slate-500">
                          Client messages with device data
                        </dt>
                        <dd className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
                          {deviceTotal}
                        </dd>
                      </div>

                      <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {deviceRows.map((row) => (
                          <li
                            key={row.key}
                            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                          >
                            <div className="text-sm font-medium text-slate-900">
                              {row.label}
                            </div>

                            <div className="mt-2 flex items-baseline gap-2 tabular-nums">
                              <span className="text-2xl font-semibold text-slate-900">
                                {row.count}
                              </span>

                              <span className="text-sm text-slate-500">
                                ({row.percent.toFixed(1)}%)
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-4">
                        <DonutChart
                          title="Client device distribution"
                          slices={deviceChartSlices}
                        />
                      </div>
                    </div>
                  )}

                  {(note || notes.length > 0) && (
                    <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
                      <span className="font-medium text-slate-900">Notes: </span>
                      {note && <div>{note}</div>}
                      {notes.length > 0 && (
                        <ul className="mt-2 list-disc space-y-1 pl-5">
                          {notes.map((n) => (
                            <li key={n}>{n}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "activities" && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Interactive tool usage
                    </h2>

                    <button
                      type="button"
                      onClick={exportActivityUsageCsv}
                      disabled={activityByType.length === 0}
                      title="Export the interactive tool usage table as CSV"
                      className="rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Export CSV
                    </button>
                  </div>

                  {activityByType.length > 0 ? (
                    <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                      <div className="max-h-[520px] overflow-y-auto print:max-h-none print:overflow-visible">
                        <table className="min-w-full divide-y divide-slate-200 text-sm">
                          <thead className="sticky top-0 z-10 bg-slate-50 print:static">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold text-slate-600">
                                Feature
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-slate-600">
                                Tool
                              </th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600">
                                Started
                              </th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600">
                                Completed
                              </th>
                              <th className="px-4 py-3 text-right font-semibold text-slate-600">
                                Submitted
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-slate-100 bg-white">
                            {activityByType.map((row, idx) => (
                              <tr
                                key={`${row.activityFamily}-${row.activityType}-${idx}`}
                              >
                                <td className="px-4 py-3 text-slate-700">
                                  {humanizeKey(row.activityFamily)}
                                </td>
                                <td className="px-4 py-3 font-medium text-slate-900">
                                  {humanizeKey(row.activityType)}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                  {row.startedCount ?? 0}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                  {row.completedCount ?? 0}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                  {row.submittedCount ?? 0}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
                      No interactive tool usage found for this range.
                    </div>
                  )}
                </div>
              )}

              {activeTab === "safety" && (
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Safety & reassurance
                  </h2>

                  {safety ? (
                    <>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <div className={safetyMetricCardClass}>
                          <div className="text-sm font-medium text-slate-900">
                            Safety detections
                          </div>
                          <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
                            {totalSafetyEvents}
                          </div>
                        </div>

                        <div className={reassuranceMetricCardClass}>
                          <div className="text-sm font-medium text-slate-900">
                            Reassurance detections
                          </div>
                          <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
                            {reassuranceDetections}
                          </div>
                        </div>

                        <div className={safetyMetricCardClass}>
                          <div className="text-sm font-medium text-slate-900">
                            Sessions with safety detections
                          </div>
                          <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
                            {sessionsWithSafetyEvents}
                          </div>
                        </div>

                        <div className={reassuranceMetricCardClass}>
                          <div className="text-sm font-medium text-slate-900">
                            Sessions with reassurance detections
                          </div>
                          <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
                            {sessionsWithReassurance}
                          </div>
                        </div>
                      </div>

                      {safetyByType.length > 0 && (
                        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm">
                          <div className="font-medium text-slate-900">
                            By event type
                          </div>
                          <ul className="mt-2 space-y-1">
                            {safetyByType.map((row, idx) => {
                              const eventType = getEventType(row) ?? "unknown";

                              return (
                                <li
                                  key={`${eventType}-${idx}`}
                                  className="flex items-center justify-between gap-3"
                                >
                                  <span className="text-slate-700">
                                    {humanizeKey(eventType)}
                                  </span>
                                  <span className="font-semibold tabular-nums text-slate-900">
                                    {row.count ?? 0}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {(lastSafetyEventAt || lastReassuranceAt) && (
                        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
                          {lastSafetyEventAt && (
                            <div>Last safety event: {lastSafetyEventAt}</div>
                          )}
                          {lastReassuranceAt && (
                            <div>
                              Last reassurance detection: {lastReassuranceAt}
                            </div>
                          )}
                        </div>
                      )}

                      <SafetyIncidentDetails details={safetyDetails} />
                    </>
                  ) : (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
                      No safety or reassurance events found for this range.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
